// Pure cumulative-line schedule shared by source rendering and native import.
export interface KineticBeat {
  text: string;
  /** 씬 상대 프레임(0 = 씬 시작). */
  atFrame: number;
  /** 이 구간이 끝나는 씬 상대 프레임(다음 구간 시작 · 마지막이면 씬 끝). 등장 리듬 자동 조절의 예산.
   *  ★없으면 «예산 무제한»으로 본다★ — 씬 끝을 모르는 호출자도 있어 기본 리듬으로 안전하게 떨어진다. */
  untilFrame?: number;
}

/** 외부(렌더 경계)에서 온 값이라 형태를 믿지 않는다 — 유효한 것만 남기고 시각 오름차순으로 세운다. */
export function normalizeBeats(raw: unknown): KineticBeat[] {
  if (!Array.isArray(raw)) return [];
  const out: KineticBeat[] = [];
  for (const b of raw) {
    if (!b || typeof b !== 'object') continue;
    const text = (b as { text?: unknown }).text;
    const at = (b as { atFrame?: unknown }).atFrame;
    if (typeof text !== 'string' || !text.trim()) continue;
    if (typeof at !== 'number' || !Number.isFinite(at)) continue;
    const until = (b as { untilFrame?: unknown }).untilFrame;
    const atR = Math.max(0, Math.round(at));
    out.push({
      text: text.trim(),
      atFrame: atR,
      // 끝이 시작보다 앞이면 버린다(뒤집힌 구간 = 예산 음수) — 예산 없음으로 떨어져 기본 리듬이 된다.
      ...(typeof until === 'number' && Number.isFinite(until) && Math.round(until) > atR
        ? { untilFrame: Math.round(until) }
        : {}),
    });
  }
  return out.sort((a, b) => a.atFrame - b.atFrame);
}

/** 구간 추출의 입력 — 세그먼트에서 이 계약이 쓰는 두 필드만(렌더러 타입에 묶이지 않게). */
export interface BeatSourceSeg {
  t?: number;
  lines?: string[];
}

/**
 * 씬 스팬 [startIdx, endIdx) → 구간 목록. ★lines 는 누적형★(브릿지 `lines.slice(0, k+1)`)이라
 * «그 세그에서 새로 붙은 줄» = 마지막 원소다. 빈 줄 세그(샘플의 빈 앵커 관용구)는 구간이 아니다.
 * 시각은 씬 앵커(startIdx) 기준 상대 프레임 — KineticTypo 가 받는 `f` 와 같은 축이다.
 */
export function sceneBeatsFrom(
  segs: ReadonlyArray<BeatSourceSeg | undefined>,
  startIdx: number,
  endIdx: number,
  fps: number,
  /** 씬이 끝나는 씬 상대 프레임 — 마지막 구간의 예산이다. 모르면 생략(그 구간만 기본 리듬). */
  sceneEndFrame?: number,
): KineticBeat[] {
  if (!Array.isArray(segs) || startIdx < 0 || !Number.isFinite(fps) || fps <= 0) return [];
  const anchorT = segs[startIdx]?.t ?? 0;
  const out: KineticBeat[] = [];
  for (let i = startIdx; i < Math.min(endIdx, segs.length); i++) {
    const ls = segs[i]?.lines ?? [];
    const text = (ls[ls.length - 1] ?? "").trim();
    if (!text) continue;
    out.push({ text, atFrame: Math.max(0, Math.round(((segs[i]?.t ?? 0) - anchorT) * fps)) });
  }
  // 구간 끝 = 다음 구간 시작 · 마지막은 씬 끝(주어졌을 때만). 예산이 이 자리에서 정해진다.
  for (let k = 0; k < out.length; k++) {
    const until = k + 1 < out.length ? out[k + 1].atFrame : sceneEndFrame;
    if (typeof until === "number" && Number.isFinite(until) && until > out[k].atFrame) out[k].untilFrame = Math.round(until);
  }
  return out;
}

